import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ArrowLeft, ArrowRight, HelpCircle, MessageSquare } from 'lucide-react'
import { RichText } from '../../components/common/RichText'
import { EvaluateResult } from '../../components/common/EvaluateResult'
import { getImageSrc } from '../../utils/imageData'
import { stripLeadingQuestionNumber } from '../../utils/questionText'
import {
  getEvaluationResult,
  getEvaluationQuestionReview,
  type EvalResultResponse,
  type EvalQuestionReview,
} from '../../api/evaluations'

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */
function QuestionImages({ images }: { images: string[] }) {
  if (!images || images.length === 0) return null
  return (
    <>
      {images.map((src, idx) => {
        const imgSrc = getImageSrc(src)
        if (!imgSrc) return null
        return (
          <Box key={idx} my={8} style={{ textAlign: 'center' }}>
            <img src={imgSrc} alt={`Figure ${idx + 1}`}
              style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid var(--sat-border-soft)' }} />
          </Box>
        )
      })}
    </>
  )
}

function ChoiceContent({ text }: { text: string }) {
  if (!text) return null
  const imageSrc = getImageSrc(text)
  if (imageSrc) {
    return <img src={imageSrc} alt="Choice" style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 4 }} />
  }
  return <RichText size="sm">{text}</RichText>
}

/* ------------------------------------------------------------------ */
/*  Grading Panel                                                       */
/* ------------------------------------------------------------------ */
function formatGradedAt(value: string | null): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

function GradingPanel({
  score,
  gradedAt,
  tutorFeedback,
}: {
  score: number | null
  gradedAt: string | null
  tutorFeedback: string | null
}) {
  const hasAny = score !== null || gradedAt !== null || (tutorFeedback && tutorFeedback.trim().length > 0)
  if (!hasAny) return null

  const gradedAtLabel = formatGradedAt(gradedAt)

  return (
    <Paper radius="xl" p="xl" withBorder style={{ background: 'var(--sat-surface)' }}>
      <Group justify="space-between" mb="md" wrap="nowrap">
        <Group gap="sm">
          <MessageSquare size={20} color="var(--sat-text)" />
          <Title order={4} c="var(--sat-text)">Tutor Grading</Title>
        </Group>
        {gradedAtLabel && (
          <Text size="sm" c="var(--sat-text-muted)">
            Graded on {gradedAtLabel}
          </Text>
        )}
      </Group>

      <Group gap="lg" mb={tutorFeedback ? 'md' : 0} align="center">
        {score !== null ? (
          <Group gap="xs" align="baseline">
            <Text size="sm" c="var(--sat-text-muted)">Score</Text>
            <Title order={2} c="var(--sat-text)">{score}</Title>
          </Group>
        ) : (
          <Badge size="lg" color="gray" variant="light" radius="xl">
            Not graded yet
          </Badge>
        )}
      </Group>

      {tutorFeedback && tutorFeedback.trim().length > 0 && (
        <Paper p="md" radius="md" style={{ background: 'var(--sat-surface-dim)' }}>
          <Text size="sm" c="var(--sat-text-muted)" mb={4}>Tutor feedback</Text>
          <RichText size="sm">{tutorFeedback}</RichText>
        </Paper>
      )}
    </Paper>
  )
}

/* ------------------------------------------------------------------ */
/*  Question Review                                                     */
/* ------------------------------------------------------------------ */
function QuestionReview({ evaluationId }: { evaluationId: number }) {
  const [q, setQ] = useState<EvalQuestionReview | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [explanationOpen, setExplanationOpen] = useState(false)
  const [explanationText, setExplanationText] = useState('')

  function fetchQuestion(p: number) {
    setLoading(true)
    getEvaluationQuestionReview(evaluationId, p)
      .then((res) => { setQ(res.data); setPage(p) })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load question', color: 'red' }))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchQuestion(1) }, [evaluationId])

  if (loading && !q) {
    return <Box style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Loader color="eveningSea" /></Box>
  }
  if (!q) return null

  const qa = q.question_answers[0]
  const isSpr = qa?.question_type === 'spr'
  const questionImageSrc = getImageSrc(qa?.question_image)
  const displayPassage = stripLeadingQuestionNumber(q.question_text)

  return (
    <Paper radius="xl" p="xl" withBorder style={{ background: 'var(--sat-surface)' }}>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          {q.module ? (
            <>
              <Badge size="lg" radius="xl" color={q.module.startsWith('RW') ? 'blue' : 'grape'} variant="light">
                {q.module.startsWith('RW') ? 'Reading & Writing' : 'Math'}
              </Badge>
              <Text size="sm" c="var(--sat-text-muted)">Module {q.module}</Text>
            </>
          ) : (
            <Badge size="lg" radius="xl" color="grapefruit" variant="light">
              Practice
            </Badge>
          )}
        </Group>
        <Text size="sm" c="var(--sat-text-muted)">Question {q.sequence} of {q.total}</Text>
      </Group>

      {/* Question */}
      <Box mb="lg">
        <QuestionImages images={q.question_images} />
        {displayPassage && <RichText size="md">{displayPassage}</RichText>}
        {questionImageSrc && (
          <Box my={8} style={{ textAlign: 'center' }}>
            <img
              src={questionImageSrc}
              alt="Question figure" style={{ maxWidth: '100%', borderRadius: 8 }}
            />
          </Box>
        )}
      </Box>

      {/* Answer result badge */}
      <Group mb="md" gap="sm">
        <Badge size="lg" color={q.is_correct ? 'green' : 'red'} variant="light">
          {q.is_correct ? 'Correct' : 'Incorrect'}
        </Badge>
        {q.user_answer ? (
          <Text size="sm" c="var(--sat-text-muted)">Your answer: <strong>{q.user_answer}</strong></Text>
        ) : (
          <Text size="sm" c="orange">No answer</Text>
        )}
      </Group>

      {/* Choices or SPR answer */}
      {isSpr ? (
        <Paper p="sm" radius="md" mb="md" style={{ background: 'var(--sat-surface-dim)' }}>
          <Text size="sm" c="var(--sat-text)">Correct answer: <strong>{qa.correct_answer_text || '—'}</strong></Text>
        </Paper>
      ) : (
        <Stack gap={6} mb="md">
          {qa?.choices.map((c) => {
            const isUserChoice = q.user_answer?.toUpperCase() === c.choice_label.toUpperCase()
            const isCorrect = c.is_correct
            let bg = 'var(--sat-surface-dim)'
            let border = '1px solid transparent'
            if (isCorrect) { bg = 'var(--sat-green-soft, rgba(34,197,94,0.1))'; border = '1px solid var(--sat-green, #22c55e)' }
            else if (isUserChoice && !isCorrect) { bg = 'rgba(239,68,68,0.08)'; border = '1px solid var(--sat-red, #ef4444)' }

            return (
              <Paper key={c.id} p="sm" radius="md" style={{ background: bg, border }}>
                <Group gap="sm" wrap="nowrap" justify="space-between">
                  <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    <Badge size="sm" variant={isUserChoice ? 'filled' : 'light'} color={isCorrect ? 'green' : isUserChoice ? 'red' : 'gray'}>
                      {c.choice_label}
                    </Badge>
                    <ChoiceContent text={c.choice_text} />
                  </Group>
                  {c.explanation && (
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      size="sm"
                      onClick={() => { setExplanationText(c.explanation!); setExplanationOpen(true) }}
                    >
                      <HelpCircle size={16} />
                    </ActionIcon>
                  )}
                </Group>
              </Paper>
            )
          })}
        </Stack>
      )}

      {/* Navigation */}
      <Group justify="space-between" mt="lg">
        <Button
          variant="light"
          color="gray"
          radius="xl"
          leftSection={<ArrowLeft size={16} />}
          disabled={page <= 1 || loading}
          onClick={() => fetchQuestion(page - 1)}
        >
          Previous
        </Button>
        <Group gap="sm">
          <Pagination
            total={q.total}
            value={page}
            onChange={(p) => fetchQuestion(p)}
            radius="xl"
            color="grapefruit"
            size="sm"
          />
          {qa?.explanation && (
            <ActionIcon
              variant="light"
              color="blue"
              radius="xl"
              size="lg"
              title="Show explanation"
              onClick={() => { setExplanationText(qa.explanation!); setExplanationOpen(true) }}
            >
              <HelpCircle size={18} />
            </ActionIcon>
          )}
        </Group>
        <Button
          variant="light"
          color="gray"
          radius="xl"
          rightSection={<ArrowRight size={16} />}
          disabled={page >= q.total || loading}
          onClick={() => fetchQuestion(page + 1)}
        >
          Next
        </Button>
      </Group>

      {/* Explanation modal */}
      <Modal opened={explanationOpen} onClose={() => setExplanationOpen(false)} title="Explanation" centered radius="lg">
        <RichText size="sm">{explanationText}</RichText>
      </Modal>
    </Paper>
  )
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                      */
/* ------------------------------------------------------------------ */
export function EvaluationDetail() {
  const { id } = useParams<{ id: string }>()
  const [result, setResult] = useState<EvalResultResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    getEvaluationResult(Number(id))
      .then((res) => setResult(res.data))
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load mock result', color: 'red' }))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  if (!result) {
    return (
      <Container size="lg" py="xl">
        <Paper radius="xl" p="xl" withBorder style={{ background: 'var(--sat-surface)', textAlign: 'center' }}>
          <Title order={2} c="var(--sat-text)" mb="md">Mock not found</Title>
          <Text c="var(--sat-text-muted)">This mock has not been scored yet or does not exist.</Text>
        </Paper>
      </Container>
    )
  }

  const normalizedAssignmentType = !result.assignment_type || result.assignment_type === 'evaluate'
    ? 'mock'
    : result.assignment_type
  const practiceMode = ['quiz', 'test', 'enhance'].includes(normalizedAssignmentType)

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Tabs defaultValue="summary" keepMounted={false}>
          <Tabs.List grow mb="md">
            <Tabs.Tab value="summary" fz="lg" fw={700}>Summary</Tabs.Tab>
            <Tabs.Tab value="review" fz="lg" fw={700}> Review</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="summary">
            <Stack gap="lg">
              <GradingPanel
                score={result.grading_score}
                gradedAt={result.graded_at}
                tutorFeedback={result.tutor_feedback}
              />
              <EvaluateResult
                title={result.title}
                rwScore={result.rw_score}
                mathScore={result.math_score}
                totalScore={result.total_score}
                totalQuestions={result.total_questions}
                masteryResults={result.mastery_results}
                isPractice={practiceMode}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="review">
            <QuestionReview evaluationId={result.evaluation_id} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  )
}
